import { workflowService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { isUserVisibleWorkflow } from '@/utils/workflow';

export const useWorkflow = (workflowId: string) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useQuery({
    queryKey: ['workflow', workflowId],
    queryFn: () =>
      workflowService.getWorkflow({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        workflow_id: workflowId,
      }),
    enabled: !!workflowId && !!selectedOrganizationId && !!selectedProgramId,
  });
};

export const useWorkflowsByProgram = (programId: string | undefined | null) => {
  const { setWorkflows, setSelectedWorkflowId, selectedWorkflowId } = useStore();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const query = useQuery({
    queryKey: queryKeys.workflows.byProgram(programId || ''),
    queryFn: () =>
      workflowService.getWorkflowsByProgram({
        organization_id: selectedOrganizationId!,
        program_id: programId!,
      }),
    enabled: !!programId && !!selectedOrganizationId,
  });

  // Sync workflows with Zustand store and handle auto-selection
  // eslint-disable-next-line complexity
  useEffect(() => {
    if (query.data?.workflows) {
      setWorkflows(query.data.workflows);

      // Check if current workflow exists in the new list
      const currentWorkflowExists =
        selectedWorkflowId &&
        query.data.workflows.some(workflow => workflow.id === selectedWorkflowId);

      // Auto-select if no workflow selected or current doesn't exist in new list
      if ((!selectedWorkflowId || !currentWorkflowExists) && query.data.workflows.length > 0) {
        // Prefer a user-visible (non-internal) workflow (matches Header combobox filter),
        // then fall back to first workflow
        const visibleWorkflow = query.data.workflows.find(isUserVisibleWorkflow);
        const workflowToSelect = visibleWorkflow || query.data.workflows[0];
        setSelectedWorkflowId(workflowToSelect?.id || null);
      }

      // Clear selection if no workflows available
      if (query.data.workflows.length === 0 && selectedWorkflowId) {
        setSelectedWorkflowId(null);
      }
    }
  }, [query.data, setWorkflows, setSelectedWorkflowId, selectedWorkflowId]);

  return {
    ...query,
    workflows: query.data?.workflows || [],
  };
};
